import type { IGroupModifierSelected, IGroupModifiers, ISelectModifiers, ISizes } from "#/types/iCardProduct";
import { applySelectedSizeToModifier } from "#/utils/syncModifierWithSize";

export const MODIFIER_CUSTOM_RENDER_SWIPER = "swiper" as const;

type WithCustomRender = { custom_render?: string | null };

const readCustomRender = (value: WithCustomRender | null | undefined): string | null => value?.custom_render ?? null;

/**
 * `custom_render` придёт с бэка на группе (ProductGroupModifier) и/или на пунктах (Modifier).
 * Пока типы сгенерируются — читаем оба места. Для swiper считаем группу кастомной,
 * если поле стоит на группе либо у всех пунктов одно и то же значение.
 */
export const getModifierGroupCustomRender = (group: IGroupModifiers | null | undefined): string | null => {
	if (!group) return null;

	const fromGroup = readCustomRender(group);
	if (fromGroup) return fromGroup;

	const modifiers = group.group_modifiers ?? [];
	if (!modifiers.length) return null;

	const values = modifiers.map((modifier) => readCustomRender(modifier)).filter((value): value is string => Boolean(value));
	if (values.length === modifiers.length && values.every((value) => value === values[0])) return values[0];

	return null;
};

export const isSwiperModifierGroup = (group: IGroupModifiers | null | undefined): boolean =>
	getModifierGroupCustomRender(group) === MODIFIER_CUSTOM_RENDER_SWIPER;

export const getRegularModifierGroups = (groups: IGroupModifiers[] | null | undefined): IGroupModifiers[] =>
	(groups ?? []).filter((group) => !isSwiperModifierGroup(group));

export const getSwiperModifierGroups = (groups: IGroupModifiers[] | null | undefined): IGroupModifiers[] => (groups ?? []).filter(isSwiperModifierGroup);

export const hasRegularModifierGroups = (groups: IGroupModifiers[] | null | undefined): boolean => getRegularModifierGroups(groups).length > 0;

export const resolveExclusiveModifierAmount = (modifier: ISelectModifiers): number => {
	if ((modifier.default_amount ?? 0) > 0) return modifier.default_amount;
	if ((modifier.min_amount ?? 0) > 0) return modifier.min_amount;
	return 1;
};

export const pickDefaultSwiperModifier = (group: IGroupModifiers): ISelectModifiers | null => {
	const modifiers = group.group_modifiers ?? [];
	if (!modifiers.length) return null;
	return modifiers.find((modifier) => (modifier.default_amount ?? 0) > 0) ?? modifiers[0];
};

export const toSelectedGroup = (
	group: Pick<IGroupModifiers, "id" | "is_fake" | "is_product_determining" | "free_quantity">,
	modifiers: ISelectModifiers[] = [],
): IGroupModifierSelected => ({
	group: group.id,
	is_fake: group.is_fake ?? false,
	is_product_determining: group.is_product_determining ?? false,
	free_quantity: group.free_quantity ?? null,
	modifier_count: modifiers.reduce((sum, modifier) => sum + (modifier.amount || 0), 0),
	modifiers,
});

export const emptyGroupSelection = (group: IGroupModifiers): IGroupModifierSelected => toSelectedGroup(group);

export const buildExclusiveGroupSelection = (group: IGroupModifiers, modifier: ISelectModifiers, selectedSize?: ISizes | null): IGroupModifierSelected => {
	const amount = resolveExclusiveModifierAmount(modifier);
	return toSelectedGroup(group, [applySelectedSizeToModifier({ ...modifier, amount }, selectedSize)]);
};

/** При синке localCounts → store не затираем выбор swiper-групп (они живут в отдельном UI). */
export const preserveSwiperGroupSelection = (
	group: IGroupModifiers,
	index: number,
	selected: IGroupModifierSelected[] | undefined,
): IGroupModifierSelected | null => {
	if (!isSwiperModifierGroup(group)) return null;
	return selected?.[index] ?? emptyGroupSelection(group);
};
